import { useRef } from 'react';

import { BodyText } from '../../typography/BodyText';
import { DisplayHeading } from '../../typography/DisplayHeading';
import { SideDrawer } from '../../layout/SideDrawer';
import { IconTickInCircle } from '../../icons/IconTickInCircle';
import { LoyaltyCta } from './LoyaltyCta';

import { useContentOverflow } from './hooks/useContentOverflow';
import { mapBasketItems } from './utils/mapBasketItems';
import { useQuickBasketContext } from './utils/QuickBasketContext';

import { QuickBasketFooter } from './QuickBasketFooter';
import { QuickBasketList } from './QuickBasketList';
import { GREETINGS_CARD_SKUS } from '../../constants/greetingsCardSkus';
import { useScrollLock } from '@virginexperiencedays/hooks';

type Props = {
  /**
   * flag whether the drawer is open or close (`isOpen` of `useDrawer()`)
   */
  isOpen: boolean;
  /**
   * method to toggle the drawer between open and close (`toggleDrawer` of `useDrawer()`)
   */
  handleIsOpen: (isOpen: boolean) => void;
  /**
   * base url
   */
  homeLink?: string;
  /**
   * flag whether the Quick basket has loyalty Greetings Card CTA
   */
  hasLoyaltyCta?: boolean;
  onLoyaltyCheckoutClick?: () => void;
  onLoyaltyEditBasketClick?: () => void;
  onLoyaltyBannerClick?: () => void;
};

const DrawerTitle = () => (
  <DisplayHeading size="2" className="text-neutral-strong">
    Basket
  </DisplayHeading>
);

export const QuickBasket = ({
  isOpen,
  handleIsOpen,
  homeLink = '',
  hasLoyaltyCta = false,
  onLoyaltyCheckoutClick,
  onLoyaltyEditBasketClick,
  onLoyaltyBannerClick,
}: Props) => {
  const { basketItems } = useQuickBasketContext();

  useScrollLock(isOpen);

  const sideDrawerRef = useRef<HTMLElement>(null);
  const items = mapBasketItems(basketItems, true);
  const hasGreetingCard = items.some((el) => GREETINGS_CARD_SKUS.has(el.sku.toLowerCase()));

  // check if the content is overflowing and set state accordingly
  const contentOverflow = useContentOverflow(sideDrawerRef, [basketItems]);

  return (
    <SideDrawer
      className="left-[unset] right-0 w-[390px] max-w-[calc(100vw_-_40px)] overflow-x-hidden"
      headerClass="px-4 md:px-4 bg-transparent"
      contentClass="px-4 md:px-4"
      flexLayout
      focusOnOpen
      allowEventDefault
      isOpen={isOpen}
      drawerTitle={DrawerTitle}
      hideDrawer={() => handleIsOpen(false)}
      ref={sideDrawerRef}
    >
      <div className="grid h-[calc(100%_-_32px)] w-full grid-cols-1 grid-rows-[auto,1fr,auto] items-start justify-between gap-4">
        <BodyText className="flex flex-row flex-nowrap items-center gap-1">
          <IconTickInCircle />
          <span className="text-neutral-strong font-semibold leading-snug">
            Item added to Basket
          </span>
        </BodyText>
        <QuickBasketList items={items} />
        {hasLoyaltyCta && !hasGreetingCard && <LoyaltyCta onClick={onLoyaltyBannerClick} />}
        <QuickBasketFooter
          items={items}
          homeLink={homeLink}
          contentOverflow={contentOverflow}
          handleIsOpen={handleIsOpen}
          hasLoyaltyCta={hasLoyaltyCta}
          onLoyaltyCheckoutClick={onLoyaltyCheckoutClick}
          onLoyaltyEditBasketClick={onLoyaltyEditBasketClick}
        />
      </div>
    </SideDrawer>
  );
};
